import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/EmptyState";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { StatusBadge } from "../../components/StatusBadge";
import { Search, Users } from "lucide-react";
import api from "../../lib/api";
import { fmtDate, money } from "../../lib/statuses";

function WaitingDays({ days }) {
  if (days == null) return <span className="text-slate-400">—</span>;
  let face = "🙂", cls = "bg-emerald-50 text-emerald-700";
  if (days > 21) { face = "😡"; cls = "bg-rose-50 text-rose-700"; }
  else if (days > 14) { face = "🙁"; cls = "bg-orange-50 text-orange-700"; }
  else if (days > 7) { face = "😐"; cls = "bg-amber-50 text-amber-700"; }
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{days} Days {face}</span>;
}

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    const { data } = await api.get(`/customers?${params}`);
    setCustomers(data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search]);

  const view = async (c) => {
    setDetail({ loading: true });
    const { data } = await api.get(`/customers/${c.id}`);
    setDetail(data);
  };

  return (
    <AdminLayout title="Customers">
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="customers-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, mobile, email..." className="pl-9" />
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Email</TableHead><TableHead className="text-center">Claims</TableHead><TableHead className="text-center">Waiting Days</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {customers === null && Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
            {customers?.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => view(c)} className="cursor-pointer hover:bg-blue-50/50">
                <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                <TableCell className="font-mono text-sm text-slate-600">{c.mobile}</TableCell>
                <TableCell className="text-sm text-slate-600">{c.email || "—"}</TableCell>
                <TableCell className="text-center"><span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{c.claim_count}</span></TableCell>
                <TableCell className="text-center"><WaitingDays days={c.waiting_days} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {customers?.length === 0 && <EmptyState icon={Users} title="No customers found" testId="customers-empty" />}
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          {detail?.customer && (
            <>
              <DialogHeader><DialogTitle>{detail.customer.name}</DialogTitle></DialogHeader>
              <div className="mb-4 flex gap-6 text-sm text-slate-600">
                <span>{detail.customer.mobile}</span><span>{detail.customer.email}</span>
              </div>
              <div className="space-y-2">
                {detail.claims.map((cl) => (
                  <button key={cl.id} onClick={() => navigate(`/admin/warranty/claims/${cl.id}`)} className="flex w-full items-center justify-between rounded-lg border border-slate-200 p-3 text-left hover:bg-slate-50">
                    <div>
                      <div className="font-mono text-sm font-semibold text-blue-700">{cl.rma_number}</div>
                      <div className="text-xs text-slate-500">{cl.product_name} • {fmtDate(cl.created_at)}</div>
                    </div>
                    <StatusBadge status={cl.status} />
                  </button>
                ))}
                {detail.claims.length === 0 && <p className="text-sm text-slate-400">No claims yet.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
